import "./SealPaymentPage.scss";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paths } from "../../../paths";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import {
  useSealRegistration,
  getDonationAmount,
  getRegistrationCategory,
} from "./SealRegistrationContext";
import { REGISTRATION_FEE_DOLLARS } from "./constants/sealRegistrationConstants";
import { SealRegistrationStepIndicator } from "./SealRegistrationStepIndicator";
import { SealInfoBox } from "./components/SealInfoBox";
import { SealRegistrationSummaryCard } from "./components/SealRegistrationSummaryCard";
import { SealPaymentSummary } from "./components/SealPaymentSummary";
import { Box } from "../../../components/styled-system/Box";
import { createCheckoutSession } from "../../../api/seal-registration-api";

export const SealPaymentPage = () => {
  const navigate = useNavigate();
  const { state } = useSealRegistration();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "OVR Seal Payment";
    return () => { document.title = "Old Vine Registry"; };
  }, []);

  // Guard: redirect if no form data submitted
  useEffect(() => {
    if (!state.formData.fullName) {
      navigate(Paths.sealRegister, { replace: true });
    }
  }, [state.formData.fullName, navigate]);

  if (!state.formData.fullName) return null;

  const donationAmount = getDonationAmount(state.formData);
  const total = REGISTRATION_FEE_DOLLARS + donationAmount;
  const category = getRegistrationCategory(state.formData.roles);
  const isTradeCategory = category === "Importing business" || category === "Retail business";
  const isWineBrandCategory = category === "Wine brand" || category === "Vineyard business";
  const packagingRequested = state.formData.intendedUse.packaging;

  const handlePayWithStripe = async () => {
    setIsProcessing(true);
    setPaymentError(null);

    try {
      const checkoutUrl = await createCheckoutSession({
        fullName: state.formData.fullName,
        jobTitle: state.formData.jobTitle,
        email: state.formData.email,
        legalOrganizationName: state.formData.legalOrganizationName,
        registeredName: state.formData.registeredName,
        organizationWebsite: state.formData.organizationWebsite,
        organizationCountry: state.formData.organizationCountry,
        roles: state.formData.roles,
        intendedUse: state.formData.intendedUse,
        packagingConfirmation: state.formData.packagingConfirmation,
        vineyardUrls: state.formData.vineyardUrls
          .map((v) => v.value)
          .filter((v) => v.trim() !== ""),
        wineNotes: state.formData.wineNotes,
        conferenceInterest: state.formData.conferenceInterest,
        communicationsOptional: state.formData.communicationsOptional,
        donationAmountInCents: donationAmount * 100,
      });

      // Redirect to Stripe's hosted checkout page
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setPaymentError(
        err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          "An unexpected error occurred. Please try again."
      );
      setIsProcessing(false);
    }
  };

  return (
    <>
      <AppSeo
        title="OVR Seal Payment"
        keywords="OVR seal, payment, old vine registry"
      />
      <div className="about-layout">
        <div className="seal-registration-content">
          <SealRegistrationStepIndicator currentStep={2} />

          <h1 className="title-large">Payment</h1>
          <p className="form-fields-sub-text" style={{ marginBottom: "16px" }}>
            Review the registration details below. When ready, click the button
            to complete payment securely through Stripe. Payment submits the
            registration for OVR review; it does not authorize immediate use of
            the seal.
          </p>

          <SealInfoBox>
            Please allow 1–3 weeks for registration and vineyard
            review/verification. The registered participant may begin using the
            OVR Seal only after OVR confirms the registration and provides the
            official artwork.
          </SealInfoBox>

          <Box display="flex" flexDirection="column" maxWidth="600px">
            {/* Registration Summary */}
            <div className="seal-payment-section-header">
              <h2 className="sub-headline">Registration summary</h2>
              <span
                className="form-fields-sub-text form-fields-sub-text-link"
                onClick={() => navigate(Paths.sealRegister)}
              >
                Edit registration details
              </span>
            </div>

            <SealRegistrationSummaryCard formData={state.formData} />

            <p className="form-fields-sub-text" style={{ marginBottom: "12px" }}>
              This registration covers only {state.formData.registeredName}. It
              does not authorize use by another wine brand or organization,
              including one under common ownership, production, management, or
              control.
            </p>

            {isWineBrandCategory && (
              <p className="form-fields-sub-text" style={{ marginBottom: "12px" }}>
                All eligible wines and vintages sold under{" "}
                {state.formData.registeredName} may use the appropriate seal.
                Other separately marketed wine brands require separate
                registrations.
              </p>
            )}

            {isTradeCategory && (
              <p className="form-fields-sub-text" style={{ marginBottom: "12px" }}>
                This registration permits {state.formData.registeredName} to use
                the seal in its own approved communications. It does not register
                the wine brands it carries or authorize those brands to use the
                seal on packaging.
              </p>
            )}

            {packagingRequested && (
              <div className="seal-packaging-notice">
                Packaging use requested for {state.formData.registeredName}.
                Each separately marketed wine brand that uses the seal on
                packaging requires its own registration.
              </div>
            )}

            {/* Payment Summary */}
            <div className="seal-form-section seal-form-section--bordered">
              <h2 className="sub-headline">Payment summary</h2>
              <div className="seal-payment-summary-box">
                <SealPaymentSummary donationAmount={donationAmount} />
              </div>
              <p className="form-fields-sub-text" style={{ marginTop: "12px" }}>
                The US $300 one-time fee covers the single wine brand, vineyard
                business, importing business, or retail business named above.
                There are no annual renewal fees.
              </p>
              <p className="form-fields-sub-text">
                Additional wine brands require separate registrations.
              </p>
              {donationAmount > 0 && (
                <p className="form-fields-sub-text">
                  Your optional donation supports the Old Vine Registry's
                  broader work to identify, document, and celebrate old
                  vineyards.
                </p>
              )}
            </div>

            {/* Final Review */}
            <p className="form-fields-sub-text">
              By completing payment, you are submitting the registration
              for OVR review. OVR may contact {state.formData.email} if
              additional vineyard or registration information is needed.
            </p>
            <p className="form-fields-sub-text">
              Review may take 1–3 weeks. Payment does not authorize{" "}
              {state.formData.registeredName} to use the OVR Seal.
            </p>
            <p className="form-fields-sub-text">
              Please wait for OVR confirmation and official artwork before
              using the seal on packaging, websites, listings, signage, or
              promotional materials.
            </p>

            <p className="form-fields-sub-text" style={{ marginTop: "12px" }}>
              You will be redirected to Stripe's secure checkout page to enter
              your payment details. Card information is handled entirely by
              Stripe — we never see or store your card data.
            </p>

            {paymentError && (
              <div className="seal-payment-error">{paymentError}</div>
            )}

            <span
              className="form-fields-sub-text form-fields-sub-text-link"
              onClick={() => navigate(Paths.sealRegister)}
              style={{
                display: "block",
                textAlign: "center",
                marginTop: "16px",
                marginBottom: "14px",
              }}
            >
              Back to edit registration
            </span>

            <button
              type="button"
              className="button seal-submit-button"
              disabled={isProcessing}
              onClick={handlePayWithStripe}
              style={{
                background: isProcessing ? "#7a8a9a" : undefined,
                cursor: isProcessing ? "not-allowed" : undefined,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              {isProcessing && <span className="seal-spinner" />}
              <span>
                {isProcessing
                  ? "Redirecting to Stripe\u2026"
                  : `Pay US $${total.toFixed(2)} with Stripe`}
              </span>
            </button>
          </Box>
        </div>
      </div>
    </>
  );
};
